"use client";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from 'react';
import { createNoise3D } from "simplex-noise";

export const WavyBackground = ({
  children,
  className,
  containerClassName,
  colors,
  waveWidth,
  backgroundFill,
  blur = 10,
  speed = "fast",
  waveOpacity = 0.5,
  ...props
}: {
  children?: any;
  className?: string;
  containerClassName?: string;
  colors?: string[];
  waveWidth?: number;
  backgroundFill?: string;
  blur?: number;
  speed?: "slow" | "fast";
  waveOpacity?: number;
  [key: string]: any;
}) => {
  const noise = createNoise3D();
  let w: number,
    h: number,
    nt: number,
    i: number,
    x: number,
    ctx: any,
    canvas: any;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Resolved CSS color strings (vars/tokens resolved via getComputedStyle so they
  // update on theme toggle — canvas 2d context can't consume `var(--…)` literals).
  const resolvedColorsRef = useRef<string[]>([]);
  const resolvedBgRef = useRef<string>('#000');
  const getSpeed = () => {
    switch (speed) {
      case "slow":
        return 0.001;
      case "fast":
        return 0.002;
      default:
        return 0.001;
    }
  };

  const CSS_VAR_RE = /var\(\s*(--[\w-]+)\s*(?:,\s*([^)]+))?\)/;

  const resolveColor = (raw: string | undefined, fallback: string) => {
    if (raw === undefined || raw === null) return fallback;
    const trimmed = raw.trim();
    if (trimmed.length === 0) return fallback;
    const match = trimmed.match(CSS_VAR_RE);
    if (match === null) return trimmed;
    const varName = match[1];
    const varFallback = match[2]?.trim() ?? fallback;
    if (varName === undefined || typeof window === 'undefined') return varFallback;
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(varName)
      .trim();
    if (value.length === 0) return varFallback;
    return value;
  };

  const defaultColors = [
    "#38bdf8",
    "#818cf8",
    "#c084fc",
    "#e879f9",
    "#22d3ee",
  ];

  const resolveAllColors = () => {
    const source = colors ?? defaultColors;
    resolvedColorsRef.current = source.map((c, idx) => {
      const fallback = defaultColors[idx % defaultColors.length] ?? '#000000';
      return resolveColor(c, fallback);
    });
    resolvedBgRef.current = resolveColor(backgroundFill, '#000');
  };

  const init = () => {
    canvas = canvasRef.current;
    ctx = canvas.getContext("2d");
    w = ctx.canvas.width = window.innerWidth;
    h = ctx.canvas.height = window.innerHeight;
    ctx.filter = `blur(${blur}px)`;
    nt = 0;
    window.onresize = function () {
      w = ctx.canvas.width = window.innerWidth;
      h = ctx.canvas.height = window.innerHeight;
      ctx.filter = `blur(${blur}px)`;
    };
    resolveAllColors();
    render();
  };

  const drawWave = (n: number) => {
    nt += getSpeed();
    const palette = resolvedColorsRef.current;
    for (i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.lineWidth = waveWidth || 50;
      ctx.strokeStyle = palette[i % palette.length];
      for (x = 0; x < w; x += 5) {
        var y = noise(x / 800, 0.3 * i, nt) * 100;
        ctx.lineTo(x, y + h * 0.5); // adjust for height, currently at 50% of the container
      }
      ctx.stroke();
      ctx.closePath();
    }
  };

  // Honour prefers-reduced-motion: draw the waves once as a static backdrop
  // and skip the rAF loop entirely for users who opt out of motion.
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;

  let animationId: number;
  const render = () => {
    ctx.fillStyle = resolvedBgRef.current;
    ctx.globalAlpha = waveOpacity || 0.5;
    ctx.fillRect(0, 0, w, h);
    drawWave(5);
    if (prefersReducedMotion === false) {
      animationId = requestAnimationFrame(render);
    }
  };

  useEffect(() => {
    init();
    // Re-resolve tokens whenever the theme class on <html> flips.
    const observer = new MutationObserver(() => {
      resolveAllColors();
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
    return () => {
      cancelAnimationFrame(animationId);
      observer.disconnect();
    };
  }, []);

  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    // I'm sorry but i have got to support it on safari.
    setIsSafari(
      typeof window !== "undefined" &&
        navigator.userAgent.includes("Safari") &&
        !navigator.userAgent.includes("Chrome")
    );
  }, []);

  return (
    <div
      className={cn(
        "h-screen flex flex-col items-center justify-center",
        containerClassName
      )}
    >
      <canvas
        className="absolute inset-0 z-0"
        ref={canvasRef}
        id="canvas"
        style={{
          ...(isSafari ? { filter: `blur(${blur}px)` } : {}),
        }}
      ></canvas>
      <div className={cn("relative z-10", className)} {...props}>
        {children}
      </div>
    </div>
  );
};
